import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useState, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import DeleteUser from "./../../assets/delete.svg";
import { DataGrid } from "@mui/x-data-grid";
import { RecommendedServicesColumns } from "../../SourceData";
import api from "../../utils/api";
import MessageModals from "../../components/MessageModals/MessageModals";
import { usePermissions } from "./../../hooks/usePermissions";
import { toast } from "react-toastify";

const RecommendedServicesView = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  // State variables
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState("all");

  const [openDelete, setOpenDelete] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Services data state
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      setPage(1);
    }
  };

  // Handle category change
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1);
  };

  // Handle status change
  const handleStatusFilterChange = (event) => {
    setSelectedStatus(event.target.value);
    setPage(1);
  };

  // Fetch recommended services data
  const fetchRecommendedServices = async () => {
    if (!hasPermission(111)) {
      return;
    }
    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      if (searchTerm) {
        params.name = searchTerm;
        params.local = "ar";
      }

      if (selectedCategory !== 10) {
        params.category = selectedCategory;
      }

      if (selectedStatus !== "all") {
        params.status = selectedStatus;
      }

      const response = await api.get("/recommended-services", { params });

      if (response.data.status === "success") {
        setServices(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching recommended services:", error);
      toast.error("حدث خطأ أثناء جلب الخدمات المقترحة");
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories for the dropdown
  const fetchCategories = async () => {
    if (!hasPermission(111)) {
      return;
    }
    try {
      const response = await api.get("/categories", {
        params: {
          limit: 100,
        },
      });

      if (response.data.status === "success") {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Handle status change for recommended service
  const handleStatusChange = async (id, newStatus) => {
    if (!hasPermission(112)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/recommended-services/${id}`, {
        status: newStatus,
      });
      toast.success("تم تغيير الحالة بنجاح");
      fetchRecommendedServices();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("حدث خطأ أثناء تغيير الحالة");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete service
  const handleDeleteService = (serviceRow) => {
    if (!hasPermission(113)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    setSelectedService(serviceRow);
    setOpenDelete(true);
  };

  // Effect to fetch services when dependencies change
  useEffect(() => {
    fetchRecommendedServices();
  }, [page, pageSize, searchTerm, selectedCategory, selectedStatus]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const actionColumn = [
    {
      field: "statusAction",
      headerName: "تغيير الحالة",
      minWidth: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        if (!hasPermission(112)) {
          return null;
        }

        const currentStatus = params?.row?.status;

        return (
          <FormControl size="small" sx={{ minWidth: 150 , display: 'flex', justifyContent: 'center' , alignItems: 'center' , height: '100%'}}>
            <Select
              value={currentStatus}
              onChange={(e) =>
                handleStatusChange(params.row._id, e.target.value)
              }
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="pending">قيد الانتظار</MenuItem>
              <MenuItem value="reviewing">قيد المراجعة</MenuItem>
              <MenuItem value="approved">موافق عليها</MenuItem>
              <MenuItem value="rejected">مرفوضة</MenuItem>
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 100,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canDelete = hasPermission(113);

        if (!canDelete) {
          return <div className="cell-action"></div>;
        }

        return (
          <div className="cell-action">
            <div
              className="cell-btn"
              onClick={() => handleDeleteService(params.row)}
            >
              <img src={DeleteUser} alt="حذف" />
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="special-header">
        <p className="title">
          الخدمات المقترحة <span className="count-badge">{totalCount || 0}</span>
        </p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث باسم الخدمة...."
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl
              sx={{
                width: isMobile ? "100%" : "180px",
              }}
            >
              <InputLabel
                htmlFor="category-select"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                الاقسام
              </InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                onChange={handleCategoryChange}
                displayEmpty={true}
                sx={{ borderRadius: "8px", height: "48px" }}
              >
                <MenuItem value={10}>الجميع</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category?.name?.ar}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                width: isMobile ? "100%" : "180px",
              }}
            >
              <InputLabel
                htmlFor="status-select"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                الحالة
              </InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={selectedStatus}
                onChange={handleStatusFilterChange}
                displayEmpty={true}
                sx={{ borderRadius: "8px", height: "48px" }}
              >
                <MenuItem value="all">الجميع</MenuItem>
                <MenuItem value="pending">قيد الانتظار</MenuItem>
                <MenuItem value="reviewing">قيد المراجعة</MenuItem>
                <MenuItem value="approved">موافق عليها</MenuItem>
                <MenuItem value="rejected">مرفوضة</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={RecommendedServicesColumns.concat(actionColumn)}
          rows={services.map((service, index) => ({
            ...service,
            id: service._id || index,
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1);
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "خدمة في الصفحة",
            },
          }}
        />

        {hasPermission(113) && (
          <MessageModals
            open={openDelete}
            currentView="delete"
            deleteMessage={"تم حذف الخدمة المقترحة بنجاح"}
            route={
              selectedService
                ? `/recommended-services/${selectedService._id}`
                : ""
            }
            onDataChange={fetchRecommendedServices}
            onClose={() => {
              setOpenDelete(false);
              setSelectedService(null);
            }}
          />
        )}
      </div>
    </>
  );
};

export default RecommendedServicesView;